import { AppError, ErrorExtensions } from '@lessonup/domain-utils';
import { groupBy } from 'lodash';
import { normalizeWordForComparison } from '../../../utils';

export class WordValidationError extends AppError {
  public constructor(message: string, extensions?: ErrorExtensions) {
    super(message, 'WORD_VALIDATION_ERROR', 400, extensions);
    Object.defineProperty(this, 'name', { value: 'WordValidationError' });
  }
}

export class MaxWordCountExceededError extends AppError {
  public constructor(maxCount: number, extensions?: ErrorExtensions) {
    super(`Cannot exceed ${maxCount} words`, 'MAX_WORD_COUNT_EXCEEDED', 400, extensions);
    Object.defineProperty(this, 'name', { value: 'MaxWordCountExceededError' });
  }
}

export namespace AnswerByCategory {
  export const type = 'answerByCategory';

  export interface Content {
    categories: Category[];
  }

  export interface Category {
    id: string;
    text: string;
  }

  export interface Word {
    word: string;
    categoryId: string;
  }

  export interface Answer {
    words?: Word[];
    modifiedAt: number;
  }

  export namespace Answer {
    export const maxWordLength = 150;
    export const maxWordCount = 200;

    export function numberOfWords(answer: Answer | undefined): number {
      return answer?.words?.length || 0;
    }

    export function isDone(answer: Answer | undefined): boolean {
      return numberOfWords(answer) > 0;
    }

    export function hasAnswer(answer: Answer | undefined): boolean {
      return isDone(answer);
    }

    export function wordsForCategory(answer: Answer | undefined, categoryId: string): Word[] {
      return answer?.words?.filter((word) => word.categoryId === categoryId) || [];
    }

    export function allWordsGroupedByCategoryId(
      answer: Answer | undefined
    ): { categoryId: string; answers: string[] }[] {
      return Object.entries(groupBy(answer?.words || [], (word) => word.categoryId)).map(([categoryId, words]) => ({
        categoryId,
        answers: words.map((word) => word.word),
      }));
    }

    export function allWords(answer: Answer | undefined): string[] {
      return answer?.words?.map((word) => word.word) || [];
    }

    export function validateWord(word: string): void {
      const trimmedWord = word.trim();

      if (!trimmedWord) {
        throw new WordValidationError('Word cannot be empty');
      }

      if (trimmedWord.length > maxWordLength) {
        throw new WordValidationError(`Word cannot exceed ${maxWordLength} characters`);
      }
    }

    export function validateWordCount(words: Word[]): void {
      if (words.length > maxWordCount) {
        throw new MaxWordCountExceededError(maxWordCount);
      }
    }

    export function findWordInCategory(words: Word[], word: string, categoryId: string): number {
      return words.findIndex((w) => w.word === word && w.categoryId === categoryId);
    }

    export function wordExistsInCategory(words: Word[], word: string, categoryId: string): boolean {
      return findWordInCategory(words, word, categoryId) !== -1;
    }

    export function moveWordToCategory(
      words: Word[],
      word: string,
      oldCategoryId: string,
      newCategoryId: string
    ): Word[] {
      const wordIndex = findWordInCategory(words, word, oldCategoryId);

      if (wordIndex === -1) {
        throw new Error('Word not found in the specified category');
      }

      if (wordExistsInCategory(words, word, newCategoryId)) {
        throw new Error('Word already exists in the target category');
      }

      const updatedWords = [...words];
      updatedWords[wordIndex] = { word, categoryId: newCategoryId };

      return updatedWords;
    }

    export function addOrReplaceWord(existingWords: Word[], word: string, categoryId: string): Word[] {
      const normalizedWord = normalizeWordForComparison(word);

      const filteredWords = existingWords.filter((w) => {
        const normalizedExisting = normalizeWordForComparison(w.word);
        return !(normalizedExisting === normalizedWord && w.categoryId === categoryId);
      });

      return [...filteredWords, { word: word.trim(), categoryId }];
    }
  }
}
